import * as React from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';

import { AutoScaledImage } from 'view/common/auto-scaled-image';
import userPickerMessages from 'view/common/fields/user-picker/messages';
import { isEmail } from 'view/common/fields/user-picker/user-picker-helper';
import type { FieldOption } from 'view/common/form-select';
import { FormAsyncMultiSelect, FormAsyncCreatableMultiSelect } from 'view/common/form-select';
import { UserPickerItem } from 'view/common/user-picker-item';
import type { ValueType } from '@atlaskit/select';
import { toMultiSelectValue } from '@atlassian/help-center-common-util/select';
import messages from './messages';
import type { FieldFormProps } from 'view/common/fields/types';

export interface User {
    value: string;
    label: string;
    iconUrl?: string;
    email: string;
}

export type MultiUserPickerOption = FieldOption<string>;
export interface Props extends FieldFormProps<string> {
    label: string;
    defaultUsers?: User[];
    canAddUserByEmailAddress: boolean;
    loadOptions: (inputValue: string, callback: (options: MultiUserPickerOption[]) => void) => void;
    doInitialSearch?: boolean; // Whether we do an initial loadOptions("")
}

export class MultiUserPickerDumb extends React.Component<Props & WrappedComponentProps> {
    onChangeForMultiSelect = (options: ValueType<MultiUserPickerOption>) => {
        this.props.onChange?.(
            this.props.id,
            toMultiSelectValue(options)
                .map((v) => v.value)
                .join(',')
        );
    };

    formatOptionLabel = ({ iconUrl, label }: MultiUserPickerOption) => {
        // Suppressing existing violation. Please fix this.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        return <UserPickerItem name={label as string} icon={iconUrl && <AutoScaledImage src={iconUrl} />} />;
    };

    formatCreateLabel = (input: string) => {
        const { intl } = this.props;
        return intl.formatMessage(messages.addAction, { value: input });
    };

    isValidNewOption = (inputValue: string, _: string, selectOptions: MultiUserPickerOption[]) => {
        // Check whether the input value already corresponds to one of the available option.
        // We check whether the email matches the input exactly otherwise we show the option to create
        const isInputValueAnOption =
            selectOptions &&
            Array.isArray(selectOptions) &&
            selectOptions.filter((option) => option.email === inputValue).length > 0;

        return !isInputValueAnOption && isEmail(inputValue);
    };

    getNoOptionsCreatable = () => {
        const { intl } = this.props;
        return intl.formatMessage(userPickerMessages.noOptionsCreatable);
    };

    getNoOptions = () => {
        const { intl } = this.props;
        return intl.formatMessage(userPickerMessages.noOptions);
    };

    render() {
        const { disabled, canAddUserByEmailAddress, defaultUsers, doInitialSearch, intl, onChange, ...otherProps } =
            this.props;
        if (canAddUserByEmailAddress) {
            return (
                <FormAsyncCreatableMultiSelect
                    {...otherProps}
                    actionSubjectId="multiUserPickerField"
                    formatOptionLabel={this.formatOptionLabel}
                    formatCreateLabel={this.formatCreateLabel}
                    isDisabled={disabled}
                    isValidNewOption={this.isValidNewOption}
                    onChange={this.onChangeForMultiSelect}
                    defaultValue={defaultUsers}
                    defaultOptions={doInitialSearch}
                    noOptionsMessage={this.getNoOptionsCreatable}
                    placeholder={intl.formatMessage(userPickerMessages.placeholder)}
                />
            );
        }

        return (
            <FormAsyncMultiSelect
                {...otherProps}
                actionSubjectId="multiUserPickerField"
                formatOptionLabel={this.formatOptionLabel}
                isDisabled={disabled}
                onChange={this.onChangeForMultiSelect}
                defaultValue={defaultUsers}
                defaultOptions={doInitialSearch}
                placeholder={intl.formatMessage(userPickerMessages.placeholder)}
                noOptionsMessage={this.getNoOptions}
            />
        );
    }
}

export default injectIntl(MultiUserPickerDumb);
